<template>
    <div
        class="basic-checkbox space-y-1"
        :class="{ 'opacity-50': disabled }"
    >
        <div class="flex">
            <div
                class="w-[20px] h-[20px] group relative"
                data-testid="checkbox-tick-input-div"
            >
                <div
                    class="relative h-full w-full flex-center border rounded bg-background-1 fade focus-outline"
                    :class="basicIconWrapperClasses"
                >
                    <BasicTransition>
                        <BasicIcon
                            v-show="localModelValue"
                            name="tick"
                            size="8"
                            class="absolute fill-white"
                        >
                        </BasicIcon>
                    </BasicTransition>
                </div>
                <input
                    :id="id"
                    :class="{ 'cursor-pointer': !disabled }"
                    :checked="localModelValue"
                    class="opacity-0 absolute h-full w-full top-0 left-0"
                    type="checkbox"
                    :disabled="disabled"
                    :required="required"
                    :aria-describedby="basicErrorMessageId"
                    :aria-invalid="hasError"
                    @keyup.space.prevent="onClick"
                    @click="onClick"
                />
            </div>

            <BasicFormControlLabel
                v-if="label || $slots.default"
                :for-id="id"
                :required="required"
                class="flex-1 mt-0.5 pl-4 !inline"
                :class="{
                    'cursor-pointer': !disabled,
                }"
            >
                <slot>
                    {{ label }}
                </slot>
            </BasicFormControlLabel>
        </div>
        <BasicErrorMessage
            v-if="hasError && computedError && basicErrorMessageId"
            :id="basicErrorMessageId"
            :error="computedError"
        ></BasicErrorMessage>
    </div>
</template>

<script setup lang="ts">
import { useBasicErrorMessage } from '@composables/useBasicErrorMessage';
import {
    useInput,
    type InputEmits,
    type InputProps,
} from '@composables/useInput';
import { useValidation } from '@composables/useValidation';

import BasicFormControlLabel from '@atoms/BasicFormControl/BasicFormControlLabel.vue';
import BasicIcon from '@atoms/BasicIcon/BasicIcon.vue';
import BasicTransition from '@atoms/BasicTransition.vue';
const BasicErrorMessage = defineAsyncComponent(
    () => import('@atoms/BasicErrorMessage/BasicErrorMessage.vue')
);

// Not sure why but `export type BasicCheckboxProps = InputProps<boolean>;` were causing errors in storybook
export interface BasicCheckboxProps extends InputProps<boolean> {}

const props = withDefaults(
    defineProps<BasicCheckboxProps>(),
    getDefaultValues()
);

const emits = defineEmits<InputEmits<BasicCheckboxProps['modelValue']>>();

const localModelValue = ref(props.modelValue || false);

const { id } = useInput(
    'basic-checkbox',
    toRef(() => props.id)
);

const { hasError, runValidation, computedError } = useValidation(
    toRef(() => props.errorMessage),
    toRef(() => props.rules),
    localModelValue
);

const { id: basicErrorMessageId } = useBasicErrorMessage(id, hasError);

const onInput = (value: boolean) => {
    if (props.disabled) return;
    emits('update:modelValue', value);
    localModelValue.value = value;
    runValidation(localModelValue.value);
};

const onClick = () => {
    onInput(!localModelValue.value);
};

const getBorderClasses = (borderProperty: string) => {
    return `${borderProperty} group-focus-within:${borderProperty}`;
};

const borderClass = computed(() => {
    if (hasError.value) return getBorderClasses('border-error');
    if (localModelValue.value) return getBorderClasses('border-primary');
    return 'border-input group-hover:border-input-hover';
});

const basicIconWrapperClasses = computed(() => {
    return [
        borderClass.value,
        {
            'bg-primary': localModelValue.value,
            'border-primary': localModelValue.value && !hasError,
        },
    ];
});

watch(
    () => props.modelValue,
    (newValue) => {
        if (localModelValue.value !== newValue) {
            onInput(newValue);
        }
    }
);
</script>
